import { useRef, useState, useEffect } from 'react';
import { VideoElement } from '../../types/player';
import { getVideoElementFromMux } from '../../utils/media-utils';

export const useVideoElement = () => {
  const playerRef = useRef<HTMLMediaElement>(null);
  const [videoElement, setVideoElement] = useState<VideoElement>(null);

  // Update video element when player ref changes
  useEffect(() => {
    console.log('Setting up video element detection...');

    // Function to find and update the video element
    const findVideoElement = () => {
      // First try to get direct video elements from Mux players
      const muxVideoElement = getVideoElementFromMux();

      if (muxVideoElement) {
        console.log('Found video element from Mux player!', muxVideoElement);
        setVideoElement(muxVideoElement);
        return true;
      }

      // Fallback to traditional methods
      // console.log('No MuxPlayer video found, looking for standard video elements');

      // Check if player ref itself is a video
      if (playerRef.current && playerRef.current.tagName.toLowerCase() === 'video') {
        console.log('Player ref is a video element');
        setVideoElement(playerRef.current);
        return true;
      }

      // Check for any video in the document as a last resort
      const anyVideo = document.querySelector('video');
      if (anyVideo) {
        console.log('Found video element in document');
        setVideoElement(anyVideo as HTMLVideoElement);
        return true;
      }

      // console.log('No video element found');
      return false;
    };

    // Try to find the video element immediately
    const foundInitially = findVideoElement();

    // If not found, set up polling to check periodically
    if (!foundInitially) {
      console.log('Video element not found initially, setting up polling');
      console.log('Polling for video element...');
      const intervalId = setInterval(() => {
        if (findVideoElement()) {
          console.log('Found video element during polling, clearing interval');
          clearInterval(intervalId);
        }
      }, 500);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, []);

  return {
    playerRef,
    videoElement,
  };
};
